import React, { useEffect, useRef, useState } from 'react';
import { rome as moment } from '../../../../utils/date';
import { useTheme } from '../../../../utils/theme-context';
import { ACTION_STATUSES } from '../../../../utils/constants/feedback-statuses';
import { IMPORTANCE_COLORS, IMPORTANCE_LEVELS } from '../../../../utils/constants/importance-rules';

const FEEDBACK_SENTIMENT = [
  { name: 'Positive', value: 'positive', colorName: 'Green', color: 'text-[#10B981]', statusColor: 'bg-[#ddf4ee]', backColor: 'bg-[#f2fbf8] dark:bg-[#278a69]', bgCell: 'bg-[#10B9811A]' },
  { name: 'Neutral', value: 'neutral', colorName: 'Amber', color: 'text-[#F59E0B]', statusColor: 'bg-[#fdf1db]', backColor: 'bg-[#fef9f3] dark:bg-[#b57b19]', bgCell: 'bg-[#F59E0B1A]' },
  { name: 'Negative', value: 'negative', colorName: 'Red', color: 'text-[#EF4444]', statusColor: 'bg-[#fcecec]', backColor: 'bg-[#fff7f7] dark:bg-[#963939]', bgCell: 'bg-[#EF44441A]' },
];

type EditField = 'category' | 'subCategory' | 'importance';

const CATEGORY_OPTIONS: Array<{
  name: string;
  value: string;
  subcategories?: { name: string; value: string }[];
}> = [
    {
      name: 'Cabin', value: 'Cabin',
      subcategories: [
        { name: 'Bathroom', value: 'bathroom' }, { name: 'Bed', value: 'bed' },
        { name: 'Temperature', value: 'temperature' }, { name: 'Comfort', value: 'comfort' },
      ],
    },
    {
      name: 'Restaurant', value: 'Restaurant',
      subcategories: [
        { name: 'Food & Beverage', value: 'Food & Beverage' }, { name: 'Timings', value: 'Timings' },
        { name: 'Service', value: 'Service' }, { name: 'Temperature', value: 'Temperature' },
        { name: 'Restaurant Staff', value: 'Restaurant Staff' }, { name: 'Menu', value: 'Menu' },
      ],
    },
    {
      name: 'Bar', value: 'Bar',
      subcategories: [
        { name: 'Choice', value: 'Choice' }, { name: 'Comfort', value: 'Comfort' },
        { name: 'Ambience', value: 'Ambience' }, { name: 'Service', value: 'Service' },
      ],
    },
    {
      name: 'Lounge', value: 'Lounge',
      subcategories: [
        { name: 'Comfort', value: 'Comfort' }, { name: 'Ambience', value: 'Ambience' },
        { name: 'Service', value: 'Service' }, { name: 'Welcome', value: 'Welcome' },
        { name: 'Food & Beverage', value: 'Food & Beverage' }, { name: 'Entertainment', value: 'Entertainment' },
      ],
    },
    {
      name: 'Boarding', value: 'Boarding',
      subcategories: [
        { name: 'Convenience', value: 'Convenience' }, { name: 'Comfort', value: 'Comfort' },
        { name: 'Service', value: 'Service' }, { name: 'Welcome', value: 'Welcome' },
      ],
    },
    {
      name: 'Onboard Experience', value: 'Onboard Experience',
      subcategories: [
        { name: 'Crew Bar', value: 'Crew Bar' }, { name: 'Crew Restaurant', value: 'Crew Restaurant' },
        { name: 'Conductor', value: 'Conductor' }, { name: 'Train Manager', value: 'Train Manager' },
        { name: 'Entertainment', value: 'Entertainment' }, { name: 'Special Events', value: 'Special Events' },
        { name: 'Train Design', value: 'Train Design' },
      ],
    },
    {
      name: 'Offboard Experience', value: 'Offboard Experience',
      subcategories: [
        { name: 'Restaurants', value: 'Restaurants' }, { name: 'Experience', value: 'Experience' },
        { name: 'Transfers', value: 'Transfers' }, { name: 'Local Guide', value: 'Local Guide' },
        { name: 'Length', value: 'Length' }, { name: 'Quality', value: 'Quality' },
        { name: 'Service & Guest Support', value: 'Service & Guest Support' },
        { name: 'Value for Money', value: 'Value for Money' }, { name: 'Content', value: 'Content' },
      ],
    },
    {
      name: 'Service', value: 'Service',
      subcategories: [
        { name: 'Guest Communication', value: 'Guest Communication' }, { name: 'Transfer', value: 'Transfer' },
      ],
    },
    { name: 'General Experience', value: 'General Experience', subcategories: [] },
  ];

const ALL_SUBCATEGORIES: { name: string; value: string }[] = (() => {
  const map = new Map<string, string>();
  CATEGORY_OPTIONS.forEach((c) =>
    (c.subcategories || []).forEach((s) => {
      if (!map.has(s.value)) map.set(s.value, s.name);
    }),
  );
  return Array.from(map.entries()).map(([value, name]) => ({ value, name }));
})();

/* ─────────────────────────────────────────────── */

const FeedbacksTable = ({
  rows,
  filterTypeSelected,
  totalCount,
  hasMore,
  loadingMore,
  openMediaModal,
  handleFilterTypeChange,
  onUpdateFeedbackCategory,
  onUpdateFeedbackImportance,
  onRowClick,
  onLoadMore,
  onFeedbackReport,
  onExportData,
  scrollContainerRef,
  journeys,
  selectedJourney,
  onJourneyFilterChange,
}: {
  rows?: any;
  filterTypeSelected?: any;
  totalCount?: number;
  hasMore?: boolean;
  loadingMore?: boolean;
  openMediaModal?: (files: any) => void;
  handleFilterTypeChange?: (type: string, name: string) => void;
  onUpdateFeedbackCategory?: (id: string, payload: { category?: string | null; subCategory?: string | null }) => Promise<any> | void;
  onUpdateFeedbackImportance?: (id: string, importance: 'High' | 'Medium' | 'Low', originalImportance?: 'High' | 'Medium' | 'Low') => Promise<any> | void;
  onRowClick?: (feedback: any) => void;
  onLoadMore?: () => void;
  onFeedbackReport?: () => void;
  onExportData?: () => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  journeys?: any[];
  selectedJourney?: any;
  onJourneyFilterChange?: (value: any) => void;
}) => {
  const { theme } = useTheme();

  // Refs for filter dropdowns
  const sentimentFilterRef = useRef<any>(null);
  const actionFilterRef = useRef<any>(null);
  const categoryFilterRef = useRef<any>(null);
  const subCategoryFilterRef = useRef<any>(null);
  const importanceFilterRef = useRef<any>(null);
  const tripFilterRef = useRef<any>(null);

  const [isSentimentFilterOpen, setIsSentimentFilterOpen] = useState(false);
  const [isActionFilterOpen, setIsActionFilterOpen] = useState(false);
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  const [isSubCategoryFilterOpen, setIsSubCategoryFilterOpen] = useState(false);
  const [isImportanceFilterOpen, setIsImportanceFilterOpen] = useState(false);
  const [isTripFilterOpen, setIsTripFilterOpen] = useState(false);

  const [ctxMenu, setCtxMenu] = useState<{
    open: boolean; rowId: string | null; field: EditField | null; x: number; y: number;
  }>({ open: false, rowId: null, field: null, x: 0, y: 0 });
  const [editValue, setEditValue] = useState('');

  const closeCtxMenu = () => setCtxMenu({ open: false, rowId: null, field: null, x: 0, y: 0 });

  const getSubcatsForCategory = (category?: string) => {
    const cat = CATEGORY_OPTIONS.find((c) => c.value === category);
    return cat?.subcategories ?? [];
  };

  const isSubcategoryValidForCategory = (category?: string, sub?: string) => {
    if (!sub) return true;
    const subs = getSubcatsForCategory(category);
    if (!subs.length) return false;
    return subs.some((s) => s.value === sub);
  };

  const selectedCategoryValue: string | undefined = filterTypeSelected?.category;
  const selectedCategory = CATEGORY_OPTIONS.find((c) => c.value === selectedCategoryValue);
  const availableSubcategories =
    selectedCategory?.subcategories && selectedCategory.subcategories.length > 0
      ? selectedCategory.subcategories
      : ALL_SUBCATEGORIES;

  const onSelectCategory = (value?: string) => {
    handleFilterTypeChange?.(value || '', 'category');
    const currentSub = filterTypeSelected?.subCategory;
    if (value) {
      const newCat = CATEGORY_OPTIONS.find((c) => c.value === value);
      const valid = (newCat?.subcategories || []).some((s) => s.value === currentSub);
      if (!valid && currentSub) handleFilterTypeChange?.('', 'subCategory');
    }
    setIsCategoryFilterOpen(false);
  };

  const onSelectSubCategory = (value?: string) => {
    handleFilterTypeChange?.(value || '', 'subCategory');
    setIsSubCategoryFilterOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (sentimentFilterRef.current && !sentimentFilterRef.current.contains(event.target)) setIsSentimentFilterOpen(false);
      if (actionFilterRef.current && !actionFilterRef.current.contains(event.target)) setIsActionFilterOpen(false);
      if (categoryFilterRef.current && !categoryFilterRef.current.contains(event.target)) setIsCategoryFilterOpen(false);
      if (subCategoryFilterRef.current && !subCategoryFilterRef.current.contains(event.target)) setIsSubCategoryFilterOpen(false);
      if (importanceFilterRef.current && !importanceFilterRef.current.contains(event.target)) setIsImportanceFilterOpen(false);
      if (tripFilterRef.current && !tripFilterRef.current.contains(event.target)) setIsTripFilterOpen(false);
    };
    const handleScroll = () => {
      setIsTripFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const capitalizeFirst = (v?: string | null) => {
    const s = (v ?? '').toString();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  };

  // Sentiment helpers
  const getSentimentColor = (sentiment?: string) => {
    const found = FEEDBACK_SENTIMENT.find((s) => s.value === sentiment);
    return found?.color || 'text-gray-400';
  };

  const getSentimentBgColor = (sentiment?: string) => {
    const found = FEEDBACK_SENTIMENT.find((s) => s.value === sentiment);
    return found?.bgCell || 'text-gray-400';
  };

  const getSentimentName = (sentiment?: string) => {
    const found = FEEDBACK_SENTIMENT.find((s) => s.value === sentiment);
    return found?.name || '';
  };

  // Action status dot color
  const getActionDotColor = (actionStatus?: string) => {
    const found = ACTION_STATUSES.find((s) => s.value === actionStatus);
    return found?.fullColor || 'bg-gray-300';
  };

  // Importance with vertical bars
  const getImportanceBars = (importance?: string) => {
    if (!importance) return null;
    const info = IMPORTANCE_COLORS[importance as keyof typeof IMPORTANCE_COLORS];
    if (!info) return null;

    // High=3 bars, Medium=2, Low=1
    const barCount = importance === 'High' ? 3 : importance === 'Medium' ? 2 : 1;
    // Bar fill color
    const barColorMap: Record<string, string> = {
      High: '#EF4444',
      Medium: '#F59E0B',
      Low: '#3B82F6',
    };
    // Pill background color
    const bgMap: Record<string, string> = {
      High: '#FEF2F2',
      Medium: '#FFFBEB',
      Low: '#EFF6FF',
    };
    // Text color
    const textColorMap: Record<string, string> = {
      High: '#EF4444',
      Medium: '#F59E0B',
      Low: '#3B82F6',
    };

    return { name: info.name, barCount, barColor: barColorMap[importance], bg: bgMap[importance], textColor: textColorMap[importance] };
  };

  /* ── Filter header icon ─────────────────────── */
  const FilterIcon = () => (
    <img
      src={theme === 'dark' ? '/assets/table-actions/filters-white.svg' : '/assets/table-actions/filters.svg'}
      className='object-contain w-[16px]'
      alt='filter'
    />
  );

  /* ── Infinite scroll handler ─────────────────── */
  const handleScroll = () => {
    if (!scrollContainerRef?.current || !hasMore || loadingMore) return;
    const el = scrollContainerRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      onLoadMore?.();
    }
  };

  return (
    <>
      {/* ── Header — outside the card ────────────────── */}
      <div className='flex items-center justify-between mb-7 mt-7 '>
        <div className='flex items-center gap-5'>
          <p className='text-[14px] font-normal text-[#717680] font-arial'>
            Found: {totalCount ?? 0} Feedbacks
          </p>
          <div className='flex items-center gap-4 ml-10'>
            {ACTION_STATUSES.map((s, idx) => (
              <div key={idx} className='flex items-center gap-1.5'>
                <span className={`w-2.5 h-2.5 rounded-full ${s.fullColor}`} />
                <span className={`text-xs ${s.color} font-inter font-normal text-[12px]`}>{s.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {onFeedbackReport && (
            <button
              onClick={onFeedbackReport}
              className='flex items-center gap-2 px-4 py-2 rounded-[10px] text-sm font-normal font-arial text-white hover:opacity-90 transition'
              style={{ backgroundColor: '#252B37' }}
            >
              <img src='/svg/brand_report.svg' className='w-5 h-5 object-contain' alt='report' />
              Guest Feedback Report
            </button>
          )}
          {onExportData && (
            <button
              onClick={onExportData}
              className='flex items-center gap-2 px-4 py-2 rounded-[10px] text-sm font-normal font-arial border border-[#E9EAEB]  text-gray-700 bg-white hover:bg-gray-50 transition'
            >
              Export data
              <img src='/svg/brand_download.svg' className='w-5 h-5 object-contain' alt='download' />
            </button>
          )}
        </div>
      </div>

      <div className='border border-gray-200 bg-white dark:bg-[#111317] dark:border-[#252B37] shadow-sm overflow-hidden' style={{ borderRadius: '16px' }}>
        {/* ── Column Headers ──────────────────────────── */}
        <div className='grid items-center border-b border-gray-100 px-2 py-3'
          style={{
            backgroundColor: theme === 'dark' ? '#111317' : '#FAFAFA',
            gridTemplateColumns: '1fr 2fr 0.8fr 1fr 1fr 1fr 0.8fr 0.8fr 1fr',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '150%',
            letterSpacing: '0%',
            color: theme === 'dark' ? '#E2E8F0' : '#414651',
          }}
        >
          {/* Trip */}
          <div className='flex items-center justify-center gap-1'>
            Trip
            <div className='relative' ref={tripFilterRef}>
              <button onClick={() => setIsTripFilterOpen(!isTripFilterOpen)} className='hover:opacity-70'>
                <FilterIcon />
              </button>
              {isTripFilterOpen && (() => {
                const rect = tripFilterRef.current?.getBoundingClientRect();
                return (
                  <div
                    className='fixed z-[9999] w-56 max-h-[300px] overflow-y-auto rounded-lg border bg-white shadow-lg p-3 text-sm space-y-1 normal-case tracking-normal'
                    style={{ top: (rect?.bottom ?? 0) + 4, left: rect?.left ?? 0 }}
                  >
                    <p className='font-medium text-gray-700 mb-1'>Filter by Trip</p>
                    <button onClick={() => { onJourneyFilterChange?.(undefined); setIsTripFilterOpen(false); }}
                      className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${!selectedJourney ? 'font-bold' : ''}`}>
                      All Trips
                    </button>
                    {(journeys || []).map((j: any) => (
                      <button key={j.value} onClick={() => { onJourneyFilterChange?.(j); setIsTripFilterOpen(false); }}
                        className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${selectedJourney?.value === j.value ? 'font-bold' : ''}`}>
                        {j.label}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          <div className='flex items-center gap-1'>Verbatim</div>
          <div className='flex items-center justify-center gap-1'>Reporter</div>

          <div className='flex items-center justify-center gap-1'>
            Carriage
          </div>

          {/* Category */}
          <div className='flex items-center justify-center gap-1'>
            Category
            <div className='relative' ref={categoryFilterRef}>
              <button onClick={() => setIsCategoryFilterOpen(!isCategoryFilterOpen)} className='hover:opacity-70'>
                <FilterIcon />
              </button>
              {isCategoryFilterOpen && (
                <div className='absolute top-6 left-0 z-20 w-56 rounded-lg border bg-white shadow-lg p-3 text-sm space-y-1 normal-case tracking-normal'>
                  <p className='font-medium text-gray-700 mb-1'>Filter by Category</p>
                  <button onClick={() => onSelectCategory('')}
                    className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${!selectedCategoryValue ? 'font-bold' : ''}`}>
                    All Categories
                  </button>
                  {CATEGORY_OPTIONS.map((item) => (
                    <button key={item.value} onClick={() => onSelectCategory(item.value)}
                      className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${selectedCategoryValue === item.value ? 'font-bold' : ''}`}>
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sub-category */}
          <div className='flex items-center justify-center gap-1'>
            Sub-cat...
            <div className='relative' ref={subCategoryFilterRef}>
              <button onClick={() => setIsSubCategoryFilterOpen(!isSubCategoryFilterOpen)} className='hover:opacity-70'>
                <FilterIcon />
              </button>
              {isSubCategoryFilterOpen && (
                <div className='absolute top-6 left-0 z-20 w-64 max-h-[300px] overflow-y-auto rounded-lg border bg-white shadow-lg p-3 text-sm space-y-1 normal-case tracking-normal'>
                  <p className='font-medium text-gray-700 mb-1'>
                    {selectedCategory ? `${selectedCategory.name} sub-categories` : 'All sub-categories'}
                  </p>
                  <button onClick={() => onSelectSubCategory('')}
                    className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${!filterTypeSelected?.subCategory ? 'font-bold' : ''}`}>
                    All Sub-categories
                  </button>
                  {availableSubcategories.length > 0 ? (
                    availableSubcategories.map((sub) => (
                      <button key={sub.value} onClick={() => onSelectSubCategory(sub.value)}
                        className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${filterTypeSelected?.subCategory === sub.value ? 'font-bold' : ''}`}>
                        {sub.name}
                      </button>
                    ))
                  ) : (
                    <div className='text-xs text-gray-400'>No sub-categories.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sentiment */}
          <div className='flex items-center justify-center gap-1'>
            Sentiment
            <div className='relative' ref={sentimentFilterRef}>
              <button onClick={() => setIsSentimentFilterOpen(!isSentimentFilterOpen)} className='hover:opacity-70'>
                <FilterIcon />
              </button>
              {isSentimentFilterOpen && (
                <div className='absolute top-6 left-0 z-20 w-48 rounded-lg border bg-white shadow-lg p-3 text-sm space-y-1 normal-case tracking-normal'>
                  <p className='font-medium text-gray-700 mb-1'>Filter</p>
                  <button onClick={() => handleFilterTypeChange?.('', 'sentiment')}
                    className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${!filterTypeSelected?.sentiment ? 'font-bold' : ''}`}>
                    All Sentiments
                  </button>
                  {FEEDBACK_SENTIMENT.map((item) => (
                    <button key={item.value} onClick={() => handleFilterTypeChange?.(item.value, 'sentiment')}
                      className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${item.color} ${filterTypeSelected?.sentiment === item.value ? 'font-bold' : ''}`}>
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Status */}
          <div className='flex items-center justify-center gap-1'>
            Action Sta...
            <div className='relative' ref={actionFilterRef}>
              <button onClick={() => setIsActionFilterOpen(!isActionFilterOpen)} className='hover:opacity-70'>
                <FilterIcon />
              </button>
              {isActionFilterOpen && (
                <div className='absolute top-6 left-0 z-20 w-48 rounded-lg border bg-white shadow-lg p-3 text-sm space-y-1 normal-case tracking-normal'>
                  <p className='font-medium text-gray-700 mb-1'>Filter</p>
                  <button onClick={() => handleFilterTypeChange?.('', 'action')}
                    className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${!filterTypeSelected?.action ? 'font-bold' : ''}`}>
                    All
                  </button>
                  <button onClick={() => handleFilterTypeChange?.('yes', 'action')}
                    className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${filterTypeSelected?.action === 'yes' ? 'font-bold' : ''}`}>
                    Yes
                  </button>
                  <button onClick={() => handleFilterTypeChange?.('no', 'action')}
                    className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${filterTypeSelected?.action === 'no' ? 'font-bold' : ''}`}>
                    No
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Importance */}
          <div className='flex items-center justify-center gap-1'>
            Importance
            <div className='relative' ref={importanceFilterRef}>
              <button onClick={() => setIsImportanceFilterOpen(!isImportanceFilterOpen)} className='hover:opacity-70'>
                <FilterIcon />
              </button>
              {isImportanceFilterOpen && (
                <div className='absolute top-6 right-0 z-20 w-52 rounded-lg border bg-white shadow-lg p-3 text-sm space-y-1 normal-case tracking-normal'>
                  <p className='font-medium text-gray-700 mb-1'>Filter</p>
                  <button onClick={() => { handleFilterTypeChange?.('', 'importance'); setIsImportanceFilterOpen(false); }}
                    className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${!filterTypeSelected?.importance ? 'font-bold' : ''}`}>
                    All Levels
                  </button>
                  {IMPORTANCE_LEVELS.map((level) => {
                    const data = IMPORTANCE_COLORS[level];
                    return (
                      <button key={level} onClick={() => { handleFilterTypeChange?.(level, 'importance'); setIsImportanceFilterOpen(false); }}
                        className={`block w-full text-left hover:bg-gray-100 p-1.5 rounded ${data.color} ${filterTypeSelected?.importance === level ? 'font-bold' : ''}`}>
                        {data.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Scrollable Rows ─────────────────────────── */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className='overflow-y-auto'
          style={{ maxHeight: '600px' }}
        >
          {rows?.map((row: any) => {
            const category = row?.additionalInfo?.category;
            const subCategory = capitalizeFirst(row?.additionalInfo?.subCategory);
            const sentiment = row?.additionalInfo?.sentiment;
            const importance = row?.additionalInfo?.importance;
            const impBars = getImportanceBars(importance);
            const rawCabin = row?.additionalInfo?.cabin;
            const cabin = typeof rawCabin === 'string' ? rawCabin : (rawCabin ? String(rawCabin) : '');
            const carriage = row?.additionalInfo?.carriage || row?.additionalInfo?.carriageType || '';

            return (
              <div key={row._id} className='border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer dark:hover:bg-[#232323]' onClick={() => onRowClick?.(row)}>
                {/* Main row */}
                <div className='grid items-start px-2 py-1 text-sm'
                  style={{ gridTemplateColumns: '1fr 2fr 0.8fr 1fr 1fr 1fr 0.8fr 0.8fr 1fr' }}
                >
                  {/* Trip */}
                  <div className='text-[#0A0D12] dark:text-white text-[12px] flex items-center justify-center font-inter font-normal h-full text-center'>
                    {row?.additionalInfo?.journeyTitle || '—'}
                  </div>

                  {/* Verbatim */}
                  <div className='pr-4' style={{ minWidth: 0 }}>
                    <div
                      className='overflow-hidden'
                      style={{
                        width: '100%',
                        maxWidth: '326px',
                        height: '76px',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: theme === 'dark' ? '#111317' : '#FAFAFA',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '130%',
                        letterSpacing: '0%',
                        color: theme === 'dark' ? '#E2E8F0' : '#0A0D12',
                      }}
                    >
                      {row?.description}
                    </div>

                    {/* Category >> Sub-category pills */}
                    <div className='flex items-center gap-2 mt-2'>
                      {category && (
                        <span className='inline-flex items-center px-2.5 py-0.5 rounded-[4px] text-[10px] font-normal bg-[#E9EAEB] text-[#535862]'
                          style={{ fontFamily: 'Arial, sans-serif' }}>
                          {category}
                        </span>
                      )}
                      {category && subCategory && (
                        <>
                          <span className='text-xs text-gray-400'>&gt;&gt;</span>
                          <span className='inline-flex items-center px-0 py-0.5 rounded-[4px] text-[10px] font-bold bg-transparent text-[#535862]'
                            style={{ fontFamily: 'Arial, sans-serif' }}>
                            {subCategory}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Date */}
                    <span className='text-xs font-normal mt-1 block font-inter text-[#717680]'>
                      {moment(row?.createdAt).format('D MMM YYYY [at] h:mm a')}
                    </span>
                  </div>

                  {/* Reporter */}
                  <div className='text-[#0A0D12] dark:text-white text-[12px] flex items-center justify-center font-inter font-normal h-full text-center'>{row?.createdBy?.username || '—'}</div>

                  {/* Carriage */}
                  <div className='flex flex-col items-center justify-center h-full text-center'>
                    {(() => {
                      const cabinLower = cabin?.toLowerCase();
                      if (cabinLower === 'restaurant' || cabinLower === 'bar') {
                        return <p className='text-gray-700 text-sm'>{cabin}</p>;
                      }
                      if (cabin) {
                        return (
                          <>
                            <p className='text-[#717680] dark:text-white font-normal font-inter text-[12px]'>Carriage {cabin.charAt(0).toUpperCase()}</p>
                            <p className='text-[#0A0D12] dark:text-white text-[12px] font-normal font-inter mt-0.5'>Cabin {cabin}</p>
                          </>
                        );
                      }
                      if (carriage) {
                        return <p className='text-gray-700 text-sm'>{carriage}</p>;
                      }
                      return <span className='text-gray-400'>—</span>;
                    })()}
                  </div>

                  {/* Category */}
                  <div
                    className='text-[#0A0D12] dark:text-white text-[12px] font-normal font-inter flex items-center justify-center h-full text-center'
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setCtxMenu({ open: true, rowId: row._id, field: 'category', x: e.clientX, y: e.clientY });
                      setEditValue(category || '');
                    }}
                    title='Right click to edit category'
                  >
                    {category || '- -'}
                  </div>

                  {/* Sub-category */}
                  <div
                    className='text-[#0A0D12] dark:text-white text-[12px] font-normal font-inter flex items-center justify-center h-full text-center'
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setCtxMenu({ open: true, rowId: row._id, field: 'subCategory', x: e.clientX, y: e.clientY });
                      setEditValue(row?.additionalInfo?.subCategory || '');
                    }}
                    title='Right click to edit sub-category'
                  >
                    {subCategory || '- -'}
                  </div>

                  {/* Sentiment */}
                  <div className={`font-normal text-[10px]  font-arial flex items-center justify-center h-full text-center  ${getSentimentColor(sentiment)} `}>
                    <div className={`p-[10px] rounded-[4px] py-1 ${getSentimentBgColor(sentiment)}`}>
                      {getSentimentName(sentiment) || '—'}
                    </div>
                  </div>

                  {/* Action Status */}
                  <div className='flex items-center justify-center gap-2 h-full'>
                    {row?.action && row?.actionStatus ? (
                      <span className={`w-3 h-3 rounded-full flex-shrink-0 ${getActionDotColor(row?.actionStatus)}`} />
                    ) : row?.action ? (
                      <span className='w-3 h-3 rounded-full flex-shrink-0 bg-gray-300' />
                    ) : (
                      <span className='w-3 h-3 rounded-full flex-shrink-0' style={{ backgroundColor: '#EF4444' }} />
                    )}
                    <span className='text-[#0A0D12] dark:text-white text-[12px] font-normal font-inter'>{row?.action ? 'Yes' : 'No'}</span>
                  </div>

                  {/* Importance — pill with vertical bars */}
                  <div
                    className='flex items-center justify-center h-full'
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setCtxMenu({ open: true, rowId: row._id, field: 'importance', x: e.clientX, y: e.clientY });
                      setEditValue(importance || 'Medium');
                    }}
                    title='Right click to edit importance'
                  >
                    {impBars ? (
                      <span
                        className='inline-flex items-center gap-2 px-3 py-1.5 rounded-[4px] text-[10px] font-normal font-arial'
                        style={{ backgroundColor: impBars.bg, color: impBars.textColor }}
                      >
                        {impBars.name}
                        {importance === 'High' && (
                          <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                            <rect x='0' y='0' width='3' height='14' rx='1.5' fill={impBars.barColor} />
                            <rect x='5' y='5' width='3' height='9' rx='1.5' fill={impBars.barColor} />
                            <rect x='10' y='9' width='3' height='5' rx='1.5' fill={impBars.barColor} />
                          </svg>
                        )}
                        {importance === 'Medium' && (
                          <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                            <rect x='1' y='4' width='12' height='2' rx='1' fill={impBars.barColor} />
                            <rect x='1' y='8' width='12' height='2' rx='1' fill={impBars.barColor} />
                          </svg>
                        )}
                        {importance === 'Low' && (
                          <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                            <path d='M3 5L7 10L11 5' stroke={impBars.barColor} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                          </svg>
                        )}
                      </span>
                    ) : (
                      <span className='text-gray-400'>—</span>
                    )}
                  </div>
                </div>

              </div>
            );
          })}

          {/* Loading indicator */}
          {loadingMore && (
            <div className='flex items-center justify-center py-6'>
              <svg className='h-5 w-5 animate-spin text-gray-400' viewBox='0 0 24 24' fill='none'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4A4 4 0 008 12H4z' />
              </svg>
              <span className='ml-2 text-sm text-gray-400'>Loading more...</span>
            </div>
          )}

          {/* No more data */}
          {!hasMore && rows?.length > 0 && (
            <div className='text-center py-4 text-xs text-gray-400'>
              All feedbacks loaded
            </div>
          )}

          {/* Empty state */}
          {(!rows || rows.length === 0) && !loadingMore && (
            <div className='text-center py-12 text-sm text-gray-400'>
              No feedbacks found
            </div>
          )}
        </div>
      </div>

      {/* ── Context Menu (Edit) ─────────────────────── */}
      {ctxMenu.open && (
        <div className='fixed inset-0 z-[9999]' onMouseDown={() => closeCtxMenu()}>
          <div
            className='absolute rounded-xl border bg-white shadow-lg p-3 text-sm min-w-[260px]'
            style={{ left: ctxMenu.x, top: ctxMenu.y }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className='flex items-center justify-between mb-2'>
              <p className='font-medium'>
                Edit {ctxMenu.field === 'category' ? 'Category' : ctxMenu.field === 'subCategory' ? 'Sub-category' : 'Importance'}
              </p>
              <button className='text-xs opacity-70 hover:opacity-100' onClick={closeCtxMenu}>✕</button>
            </div>

            {ctxMenu.field === 'importance' ? (
              <select className='w-full rounded-lg border px-2 py-2' value={editValue} onChange={(e) => setEditValue(e.target.value)}>
                {IMPORTANCE_LEVELS.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            ) : ctxMenu.field === 'category' ? (
              <select className='w-full rounded-lg border px-2 py-2' value={editValue} onChange={(e) => setEditValue(e.target.value)}>
                <option value=''>(None)</option>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.name}</option>
                ))}
              </select>
            ) : (
              (() => {
                const row = rows?.find((r: any) => r._id === ctxMenu.rowId);
                const currentCategory = row?.additionalInfo?.category;
                const subs = getSubcatsForCategory(currentCategory);
                const options = subs.length ? subs : ALL_SUBCATEGORIES;
                return (
                  <select className='w-full rounded-lg border px-2 py-2' value={editValue} onChange={(e) => setEditValue(e.target.value)}>
                    <option value=''>(None)</option>
                    {options.map((s) => (
                      <option key={s.value} value={s.value}>{s.name}</option>
                    ))}
                  </select>
                );
              })()
            )}

            <div className='flex gap-2 mt-3 justify-end'>
              <button className='px-3 py-2 rounded-lg border hover:bg-gray-50' onClick={closeCtxMenu}>Cancel</button>
              <button
                className='px-3 py-2 rounded-lg bg-gray-900 text-white hover:opacity-90'
                onClick={async () => {
                  const row = rows?.find((r: any) => r._id === ctxMenu.rowId);
                  if (!row) return closeCtxMenu();

                  if (ctxMenu.field === 'importance') {
                    const originalImportance = row?.additionalInfo?.importance;
                    const newImportance = editValue as 'High' | 'Medium' | 'Low';
                    await onUpdateFeedbackImportance?.(row._id, newImportance, originalImportance);
                  } else {
                    const currentCategory = row?.additionalInfo?.category || '';
                    const currentSub = row?.additionalInfo?.subCategory || '';
                    let nextCategory = currentCategory;
                    let nextSub = currentSub;

                    if (ctxMenu.field === 'category') {
                      nextCategory = editValue || '';
                      if (!isSubcategoryValidForCategory(nextCategory, nextSub)) nextSub = '';
                    } else if (ctxMenu.field === 'subCategory') {
                      nextSub = editValue || '';
                    }

                    await onUpdateFeedbackCategory?.(row._id, {
                      category: nextCategory || null,
                      subCategory: nextSub || null,
                    });
                  }
                  closeCtxMenu();
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbacksTable;
